import { useEffect, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { resolveTenantSlug } from '../../lib/tenant'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import { useOutboxStore } from '../../stores/outboxStore'
import OutboxFlusher from '../OutboxFlusher'
import TawkWidget from '../TawkWidget'
import InfoTip from '../InfoTip'
import HelpHintBanner from '../HelpHintBanner'
import {
  Menu,
  X,
  Home,
  Users,
  Calendar,
  FileText,
  DollarSign,
  Settings,
  LogOut,
  Shield,
  ChevronDown,
  Bell,
  ClipboardList,
  FileCheck,
} from 'lucide-react'

interface NavItem {
  name: string
  href: string
  icon: React.ElementType
  roles?: string[]
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/app/dashboard', icon: Home },
  { name: 'Clients', href: '/app/clients', icon: Users },
  { name: 'Schedule', href: '/app/schedule', icon: Calendar },
  { name: 'Sessions', href: '/app/sessions', icon: ClipboardList },
  { name: 'Clinical Notes', href: '/app/notes', icon: FileText, roles: ['admin', 'bcba', 'rbt'] },
  { name: 'Treatment Plans', href: '/app/treatment-plans', icon: FileCheck, roles: ['admin', 'bcba'] },
  { name: 'Authorizations', href: '/app/authorizations', icon: Shield, roles: ['admin', 'bcba', 'billing'] },
  { name: 'Billing', href: '/app/billing', icon: DollarSign, roles: ['admin', 'billing', 'bcba'] },
  { name: 'Compliance', href: '/app/compliance', icon: Shield, roles: ['admin', 'billing'] },
  { name: 'Settings', href: '/app/settings', icon: Settings, roles: ['admin'] },
]

export default function AppLayout({ children }: { children?: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const tenantSlug = resolveTenantSlug()
  const isOnline = useOnlineStatus()
  const outboxCount = useOutboxStore((s) => s.pendingCount())
// BEGIN PWA HARDENING
  const [connectivityBanner, setConnectivityBanner] = useState<"offline" | "reconnected" | null>(null)

  useEffect(() => {
    if (!isOnline) {
      setConnectivityBanner("offline")
      return
    }
    if (connectivityBanner === "offline") {
      const timer = window.setTimeout(() => setConnectivityBanner(null), 3500)
      setConnectivityBanner("reconnected")
      return () => window.clearTimeout(timer)
    }
    setConnectivityBanner(null)
  }, [isOnline, connectivityBanner])
// END PWA HARDENING

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const filteredNavigation = navigation.filter(
    (item) => !item.roles || (user && item.roles.includes(user.role))
  )

  return (
    <div className="min-h-screen bg-gray-50">
      <OutboxFlusher />
      <TawkWidget />
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="relative z-50 lg:hidden">
          <div className="fixed inset-0 bg-gray-900/80" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-0 flex">
            <div className="relative mr-16 flex w-full max-w-xs flex-1">
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                <button
                  type="button"
                  className="-m-2.5 p-2.5"
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="sr-only">Close sidebar</span>
                  <X className="h-6 w-6 text-white" aria-hidden="true" />
                </button>
              </div>

              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                <div className="flex h-16 shrink-0 items-center">
                  <Shield className="h-8 w-8 text-primary-600" />
                  <span className="ml-2 font-semibold text-lg">Grace Alone ABA</span>
                </div>
                <nav className="flex flex-1 flex-col">
                  <ul className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul className="-mx-2 space-y-1">
                        {filteredNavigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={`group flex gap-x-3 rounded-md p-2 text-sm font-medium ${
                                location.pathname === item.href
                                  ? 'bg-primary-50 text-primary-600'
                                  : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                              }`}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <item.icon className="h-5 w-5 shrink-0" />
                              {item.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4">
          <div className="flex h-16 shrink-0 items-center">
            <Shield className="h-8 w-8 text-primary-600" />
            <span className="ml-2 font-semibold text-lg">Grace Alone ABA</span>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul className="-mx-2 space-y-1">
                  {filteredNavigation.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={`group flex gap-x-3 rounded-md p-2 text-sm font-medium ${
                          location.pathname === item.href
                            ? 'bg-primary-50 text-primary-600'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                        }`}
                      >
                        <item.icon className="h-5 w-5 shrink-0" />
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>

              <li className="mt-auto">
                <button
                  onClick={handleLogout}
                  className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-red-600 w-full"
                >
                  <LogOut className="h-5 w-5 shrink-0" />
                  Sign out
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 border-b border-gray-200 bg-white shadow-sm pwa-safe-top">
          {/* Soft launch banner */}
          <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-xs">
            <div className="mx-auto max-w-7xl px-4 py-1.5">
              Soft launch: features are rolling out progressively; some areas may be limited or read-only.
            </div>
          </div>
          {/* Hover-info hint banner */}
          <HelpHintBanner />
          {/* BEGIN PWA HARDENING */}
          {connectivityBanner === 'offline' && (
            <div className="bg-amber-100 border-b border-amber-200 text-amber-900 text-xs">
              <div className="mx-auto max-w-7xl px-4 py-1.5">Connection lost - working locally. Drafts will sync when back online.</div>
            </div>
          )}
          {connectivityBanner === 'reconnected' && (
            <div className="bg-emerald-50 border-b border-emerald-200 text-emerald-900 text-xs">
              <div className="mx-auto max-w-7xl px-4 py-1.5">Reconnected - syncing drafts now.</div>
            </div>
          )}
          {/* END PWA HARDENING */}
          <div className="flex h-14 shrink-0 items-center gap-x-4 px-4 sm:gap-x-6 sm:px-6 lg:px-8">
            <button
              type="button"
              className="-m-2.5 p-2.5 text-gray-700 lg:hidden touch-target"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Menu className="h-6 w-6" aria-hidden="true" />
            </button>

            <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
              <div className="flex flex-1 items-center">
                <div className="flex items-center gap-2">
                  {tenantSlug && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                      Tenant: {tenantSlug}
                      <InfoTip text="Your organization's URL slug. Staff log in by typing this on the sign-in page. It identifies which practice you're working in." />
                    </span>
                  )}
                  {!isOnline && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-xs font-medium text-amber-800">
                      Offline
                      <InfoTip text="No internet connection detected. You can keep working — drafts are saved locally and will upload automatically when you reconnect." />
                    </span>
                  )}
                  {outboxCount > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 border border-blue-200 px-2 py-1 text-xs font-medium text-blue-800">
                      Sync queue: {outboxCount}
                      <InfoTip text={`${outboxCount} record(s) saved locally and waiting to upload to the server. They'll send automatically when you're online.`} />
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-x-4 lg:gap-x-6">
                {/* Notifications */}
                <InfoTip text="Notifications: alerts about authorization expirations, claim denials, session signoff requests, and other items needing your attention.">
                  <button
                    type="button"
                    className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500 touch-target cursor-help"
                  >
                    <span className="sr-only">View notifications</span>
                    <Bell className="h-6 w-6" aria-hidden="true" />
                  </button>
                </InfoTip>

                {/* User menu */}
                <div className="relative">
                  <button
                    type="button"
                    className="flex items-center gap-x-2 rounded-full text-sm focus:outline-none touch-target"
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                  >
                    <span className="sr-only">Open user menu</span>
                    <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white font-medium">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </div>
                    <span className="hidden lg:flex lg:items-center">
                      <span className="text-sm font-medium text-gray-700">
                        {user?.full_name}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 text-gray-400" />
                    </span>
                  </button>

                  {userMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setUserMenuOpen(false)}
                      />
                      <div className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                          <p className="text-xs text-gray-500">{user?.role_display}</p>
                        </div>
                        <Link
                          to="/app/profile"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          Your Profile
                        </Link>
                        <button
                          onClick={() => {
                            setUserMenuOpen(false)
                            handleLogout()
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Sign out
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <main className="py-6 px-4 sm:px-6 lg:px-8">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  )
}


